import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateCustomerPayload,
  CustomerDetail,
  CustomerListItem,
  CustomerWritePayload,
  ImportVCardResult,
  ImportVCardRow,
  PagedResult
} from './types';

interface CustomerListParams {
  search: string;
  includeInactive: boolean;
  page: number;
  pageSize: number;
}

export function useCustomerList(params: CustomerListParams) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: async () => {
      const res = await apiClient.get<PagedResult<CustomerListItem>>('/api/v1/customers', {
        params: {
          search: params.search.length >= 2 ? params.search : undefined,
          includeInactive: params.includeInactive,
          page: params.page,
          pageSize: params.pageSize
        }
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 15_000
  });
}

export interface ClientSummary {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  totalPaid: number;
  totalOutstanding: number;
  visitCount: number;
  lastVisitStartUtc: string | null;
}

interface ClientsSummaryResult {
  page: number;
  pageSize: number;
  total: number;
  clients: ClientSummary[];
}

/** Per-client aggregates for the main clients view (Pack B Item 5). */
export function useClientsSummary(params: CustomerListParams) {
  return useQuery({
    queryKey: ['clients-summary', params],
    queryFn: async () => {
      const res = await apiClient.get<ClientsSummaryResult>('/api/v1/reports/clients', {
        params: {
          search: params.search.length >= 2 ? params.search : undefined,
          includeInactive: params.includeInactive,
          page: params.page,
          pageSize: params.pageSize
        }
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 15_000
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      const res = await apiClient.get<CustomerDetail>(`/api/v1/customers/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 15_000
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCustomerPayload) => {
      const res = await apiClient.post<CustomerDetail>('/api/v1/customers', payload);
      return res.data;
    },
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.setQueryData(['customer', detail.id], detail);
    }
  });
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CustomerWritePayload) => {
      await apiClient.put(`/api/v1/customers/${id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
    }
  });
}

export function useDeactivateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.patch(`/api/v1/customers/${id}/deactivate`);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
    }
  });
}

export function useReactivateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.patch(`/api/v1/customers/${id}/reactivate`);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
    }
  });
}

export function useBulkImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: ImportVCardRow[]) => {
      const res = await apiClient.post<ImportVCardResult>(
        '/api/v1/customers/bulk-import',
        { rows }
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] })
  });
}
